import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { api, fmtDate, fmtEuro } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InlineCatalog } from "@/components/InlineCatalog";
import { PageHeader, Panel, GoldButton, GhostButton, Empty } from "@/components/common";

export const TYPE_LABEL = { laser: "Epilazione laser", viso: "Pacchetto viso", corpo: "Pacchetto corpo" };
export const QUOTE_STATUS = { bozza: "Bozza", inviato: "Inviato", accettato: "Accettato", rifiutato: "Rifiutato" };
const STATUS_CLS = { bozza: "border-[#E8E1D7] text-[#6E675F]", inviato: "border-[#4A6B82]/30 text-[#4A6B82]", accettato: "border-[#2D6A4F]/30 text-[#2D6A4F] bg-[#2D6A4F]/5", rifiutato: "border-red-200 text-red-700" };

export const QuoteStatus = ({ s }) => <span className={`rounded-full border px-2.5 py-0.5 text-xs ${STATUS_CLS[s]}`}>{QUOTE_STATUS[s]}</span>;

export const QuoteList = ({ items, showClient = true }) => (
  items.length === 0 ? <Empty testId="quotes-empty">Nessun preventivo.</Empty> : (
    <Panel className="p-2">
      <ul className="divide-y divide-[#E8E1D7]">
        {items.map((q) => (
          <li key={q.id}>
            <Link to={`/preventivi/${q.id}`} className="flex flex-wrap items-center gap-4 rounded-xl px-4 py-3 hover:bg-[#FDFBF7]" data-testid={`quote-row-${q.id}`}>
              <div className="w-28 font-mono text-xs text-[#6E675F]">{q.number}</div>
              <div className="flex-1 min-w-[160px]">
                <div className="font-medium text-[#121212]">{q.title || TYPE_LABEL[q.type]}</div>
                <div className="text-xs text-[#6E675F]">{showClient ? `${q.client_name} · ` : ""}{TYPE_LABEL[q.type]} · {fmtDate(q.created_at)}</div>
              </div>
              <QuoteStatus s={q.status} />
              <div className="w-24 text-right font-serif text-xl">{fmtEuro(q.total)}</div>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  )
);

export default function Quotes() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const nav = useNavigate();
  useEffect(() => { api.get("/quotes").then((r) => setItems(r.data)); }, []);
  const shown = filter ? items.filter((q) => q.type === filter) : items;

  return (
    <div data-testid="quotes-page">
      <PageHeader overline="Preventivi" title="Laser e pacchetti">
        <GoldButton onClick={() => nav("/preventivi/nuovo?type=laser")} data-testid="new-laser-quote-button"><Plus className="h-4 w-4" /> Preventivo laser</GoldButton>
        <GhostButton onClick={() => nav("/preventivi/nuovo?type=viso")} data-testid="new-package-quote-button"><Plus className="h-4 w-4" /> Pacchetto viso/corpo</GhostButton>
      </PageHeader>
      <Tabs defaultValue="elenco">
        <TabsList className="mb-6 bg-[#F6F1EA] p-1 rounded-full">
          <TabsTrigger value="elenco" className="rounded-full px-4 data-[state=active]:bg-[#121212] data-[state=active]:text-[#F6F1EA]" data-testid="tab-quotes-list">Preventivi</TabsTrigger>
          <TabsTrigger value="zone" className="rounded-full px-4 data-[state=active]:bg-[#121212] data-[state=active]:text-[#F6F1EA]" data-testid="tab-laser-zones">Zone laser</TabsTrigger>
        </TabsList>
        <TabsContent value="elenco">
          <div className="mb-4 flex flex-wrap gap-2">
            {[["", "Tutti"], ["laser", "Laser"], ["viso", "Viso"], ["corpo", "Corpo"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`quotes-filter-${v || "all"}`} className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${filter === v ? "border-[#C5A059] bg-[#121212] text-[#F6F1EA]" : "border-[#E8E1D7] bg-white text-[#6E675F]"}`}>{l}</button>
            ))}
          </div>
          <QuoteList items={shown} />
        </TabsContent>
        <TabsContent value="zone">
          <p className="mb-4 text-sm text-[#6E675F]">Le zone del corpo con il prezzo a seduta e le sedute consigliate. Le usi per comporre i preventivi laser.</p>
          <InlineCatalog resource="laser_zones" testPrefix="zone" blank={{ name: "", price: "", sessions: 6 }} emptyText="Nessuna zona. Aggiungi la prima qui sotto (es. Gambe intere, Ascelle, Inguine)."
            fields={[{ key: "name", label: "Zona" }, { key: "price", label: "Prezzo a seduta €", type: "number", className: "w-36" }, { key: "sessions", label: "Sedute consigliate", type: "number", className: "w-36" }]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
